/**
 * Users page — operational counterpart to the Groups "ledger" page.
 *
 * Shares the ledger palette and serif/mono typography, but stays
 * deliberately plain: a roster you scan, an invite panel you open
 * when you need it, and a pending list with single-click cancel.
 *
 * Permission shape (mirrors policy/invite procedure):
 *   org_admin    → invite into any group in the tenant, any tenant role
 *   group_admin  → invite into the groups they administer, normal role
 *   normal user  → read-only
 *
 * The page is most useful to org-admins and multi-group admins; for
 * a single-group admin it collapses naturally to that one scope.
 *
 * All reads are tenant-wide (ignore active-group dropdown).
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ApiError,
  tenantApi,
  type GroupRow,
  type PendingInvitation,
  type UserRow,
} from '../api.js';
import { useTenantSession } from '../auth-context.js';
import { useInvalidate, useTenantQuery } from '../query.js';
import { usePageTitle } from '../use-page-title.js';
import { hasInviteErrors, inviteFieldErrors } from './invite-validation.js';

// Swedish copy for the stable error codes the backend maps failures to.
// Covers the Clerk invitation codes (REGTA-64) and PRODUCT_NOT_SUBSCRIBED
// (the identity-center subscription gap, REGTA-91) — the latter must read as a
// fixable config gap, not a generic permission error. Unknown codes fall back
// to the backend message, which is already human-readable English.
const ERROR_SV: Record<string, string> = {
  ORG_MEMBER_LIMIT_REACHED:
    'Organisationen har nått sitt medlemstak. Ta bort en medlem eller kontakta supporten för att höja gränsen.',
  INVITATION_ALREADY_PENDING:
    'Det finns redan en väntande inbjudan till den här e-postadressen. Återkalla den innan du skickar en ny.',
  ALREADY_A_MEMBER: 'Den här e-postadressen tillhör redan en medlem i organisationen.',
  PRODUCT_NOT_SUBSCRIBED:
    'Organisationen saknar en aktiv Regta-prenumeration, så användare kan inte hanteras här. Kontakta supporten för att aktivera den.',
};

function errorTextSv(e: unknown): string {
  const sv = e instanceof ApiError ? ERROR_SV[e.code] : undefined;
  return sv ?? (e as Error).message;
}

export function UsersPage() {
  usePageTitle('Användare');
  const { token, actor } = useTenantSession();
  const invalidate = useInvalidate();

  const usersQ = useTenantQuery(
    'users.list',
    () => tenantApi.users.list(),
    [],
    { ignoreActiveGroup: true },
  );
  const groupsQ = useTenantQuery(
    'groups.list',
    () => tenantApi.groups.list(),
    [],
    { ignoreActiveGroup: true },
  );
  const invitationsQ = useTenantQuery(
    'users.list_invitations',
    () => tenantApi.users.list_invitations(),
    [],
    { ignoreActiveGroup: true },
  );

  const users: UserRow[] | null = usersQ.data?.users ?? null;
  const groups: GroupRow[] | null = groupsQ.data?.groups ?? null;
  const invitations: PendingInvitation[] | null = invitationsQ.data?.invitations ?? null;

  const isOrgAdmin = actor?.tenantRole === 'org_admin';
  const myAdminGroupIds = useMemo(
    () => new Set((actor?.memberships ?? []).filter((m) => m.role === 'admin').map((m) => m.groupId)),
    [actor],
  );
  const isGroupAdmin = !isOrgAdmin && myAdminGroupIds.size > 0;
  const canInvite = isOrgAdmin || isGroupAdmin;

  // What groups the *inviter* may seat invitees into.
  const assignableGroups = useMemo(() => {
    if (!groups) return [] as GroupRow[];
    if (isOrgAdmin) return groups;
    return groups.filter((g) => myAdminGroupIds.has(g.id));
  }, [groups, isOrgAdmin, myAdminGroupIds]);

  // Pending-invitation cancel scope (group-admin can only cancel
  // invites entirely within groups they administer).
  const canCancel = (inv: PendingInvitation): boolean => {
    if (isOrgAdmin) return true;
    const ids = inv.public_metadata.group_ids ?? [];
    return ids.length > 0 && ids.every((id) => myAdminGroupIds.has(id));
  };

  const [inviteOpen, setInviteOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [tenantRole, setTenantRole] = useState<'org_admin' | 'normal'>('normal');
  const [groupRole, setGroupRole] = useState<'admin' | 'normal'>('normal');
  const [groupIds, setGroupIds] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  // Group-admin who admins exactly one group: preselect it so the
  // form has zero ceremony.
  const inviteGroupIds = useMemo(() => {
    if (groupIds.length > 0) return groupIds;
    if (!isOrgAdmin && assignableGroups.length === 1) {
      return [assignableGroups[0]!.id];
    }
    return groupIds;
  }, [groupIds, isOrgAdmin, assignableGroups]);

  const resetForm = () => {
    setEmail('');
    setName('');
    setGroupIds([]);
    setTenantRole('normal');
    setGroupRole('normal');
    setMsg(null);
  };

  const invite = async () => {
    if (!token || !email.trim()) return;
    if (!name.trim()) {
      setErr('Ange namn på användaren.');
      return;
    }
    if (inviteGroupIds.length === 0) {
      setErr('Välj minst en grupp för användaren.');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await tenantApi.users.invite({
        email: email.trim(),
        name: name.trim(),
        group_ids: inviteGroupIds,
        tenant_role: isOrgAdmin ? tenantRole : 'normal',
        group_role: groupRole,
      });
      resetForm();
      setInviteOpen(false);
      setMsg('Inbjudan skickad.');
      await invalidate('users.list_invitations');
    } catch (e) {
      setErr(errorTextSv(e));
    } finally {
      setBusy(false);
    }
  };

  const cancelInvitation = async (invId: string, email: string) => {
    if (!token) return;
    if (!window.confirm(`Återkalla inbjudan till ${email}?`)) return;
    setBusy(true);
    setErr(null);
    try {
      await tenantApi.users.cancel_invitation({ invitation_id: invId });
      await invalidate('users.list_invitations');
      setMsg(`Inbjudan till ${email} återkallad.`);
    } catch (e) {
      setErr(errorTextSv(e));
    } finally {
      setBusy(false);
    }
  };

  const groupNameById = useMemo(
    () => new Map((groups ?? []).map((g) => [g.id, g.name])),
    [groups],
  );
  const isDefaultById = useMemo(
    () => new Map((groups ?? []).map((g) => [g.id, g.is_default])),
    [groups],
  );

  const filterTokens = useMemo(
    () =>
      query
        .trim()
        .toLowerCase()
        .split(/\s+/)
        .filter(Boolean),
    [query],
  );
  const matchesQuery = (haystacks: ReadonlyArray<string | null | undefined>): boolean => {
    if (filterTokens.length === 0) return true;
    const blob = haystacks
      .filter((s): s is string => typeof s === 'string' && s.length > 0)
      .join(' ')
      .toLowerCase();
    return filterTokens.every((t) => blob.includes(t));
  };

  const filteredUsers = useMemo(() => {
    if (!users) return null;
    return users.filter((u) =>
      matchesQuery([
        u.name,
        u.email,
        u.tenantRole,
        ...u.memberships.map((m) => groupNameById.get(m.groupId) ?? null),
      ]),
    );
  }, [users, groupNameById, filterTokens]);
  const filteredInvitations = useMemo(() => {
    if (!invitations) return null;
    return invitations.filter((inv) =>
      matchesQuery([
        inv.public_metadata.name,
        inv.email_address,
        inv.public_metadata.tenant_role,
        ...(inv.public_metadata.group_ids ?? []).map((id) => groupNameById.get(id) ?? null),
      ]),
    );
  }, [invitations, groupNameById, filterTokens]);

  const queryErr = usersQ.error ?? groupsQ.error ?? invitationsQ.error;
  const displayErr = err ?? (queryErr ? errorTextSv(queryErr) : null);

  const pendingCount = invitations?.length ?? 0;

  // What the actor's scope looks like in words — surfaces just above the
  // roster so a group-admin understands why their invite list is short.
  const scopeLine: string = (() => {
    if (isOrgAdmin) return 'Som org-admin ser och bjuder du in i hela organisationen.';
    if (isGroupAdmin) {
      const names = (groups ?? [])
        .filter((g) => myAdminGroupIds.has(g.id))
        .map((g) => g.name);
      const list = names.length <= 2 ? names.join(' och ') : `${names.slice(0, -1).join(', ')} och ${names.at(-1)}`;
      return `Som gruppadmin kan du bjuda in användare till ${list}. Användarlistan visar hela organisationen — du kan inte ändra den.`;
    }
    return 'Du har läsbehörighet. Be en org-admin eller gruppadmin om hjälp med att bjuda in nya användare.';
  })();

  return (
    <div className="ledger ledger-spine -mx-6 -my-6 min-h-[calc(100vh-3.5rem)] px-6 py-10">
      <Masthead
        count={users?.length ?? null}
        pendingCount={pendingCount}
        canInvite={canInvite}
        inviteOpen={inviteOpen}
        onInvite={() => setInviteOpen((v) => !v)}
        busy={busy}
      />

      <p
        className="mt-4 max-w-2xl pl-14 text-[13px]"
        style={{
          color: 'var(--ledger-ink-soft)',
          fontFamily: 'var(--ledger-serif)',
          fontStyle: 'italic',
          fontVariationSettings: "'opsz' 18, 'wght' 360",
        }}
      >
        {scopeLine}
      </p>

      {displayErr && (
        <ErrorStrip message={displayErr} onDismiss={() => setErr(null)} tone="ember" />
      )}
      {!displayErr && msg && (
        <ErrorStrip message={msg} onDismiss={() => setMsg(null)} tone="muted" />
      )}

      {canInvite && inviteOpen && (
        <InvitePanel
          email={email}
          onEmailChange={setEmail}
          name={name}
          onNameChange={setName}
          tenantRole={tenantRole}
          onTenantRoleChange={setTenantRole}
          showOrgAdminCheckbox={isOrgAdmin}
          groupRole={groupRole}
          onGroupRoleChange={setGroupRole}
          assignableGroups={assignableGroups}
          selectedGroupIds={inviteGroupIds}
          onSelectedGroupIdsChange={setGroupIds}
          busy={busy}
          onCancel={() => {
            setInviteOpen(false);
            resetForm();
          }}
          onSubmit={() => void invite()}
        />
      )}

      <section className="mt-10">
        <SectionHeader
          label="Aktiva användare"
          count={users?.length ?? null}
          filteredCount={
            filterTokens.length > 0 && filteredUsers
              ? filteredUsers.length
              : null
          }
        />
        {users && users.length > 0 && (
          <div className="mt-4 pl-14">
            <SearchInput
              value={query}
              onChange={setQuery}
              placeholder="Sök på namn, e-post eller grupp"
            />
          </div>
        )}
        {users === null ? (
          <div className="ledger-meta mt-6 pl-14">Laddar…</div>
        ) : users.length === 0 ? (
          <div className="mt-6 pl-14">
            <p
              style={{
                fontFamily: 'var(--ledger-serif)',
                fontSize: '16px',
                fontStyle: 'italic',
                color: 'var(--ledger-ink-soft)',
              }}
            >
              Inga användare ännu.
            </p>
          </div>
        ) : filteredUsers && filteredUsers.length === 0 ? (
          <div className="ledger-meta mt-6 pl-14">
            Inga användare matchar “{query.trim()}”.
          </div>
        ) : (
          <ol className="mt-6">
            {(filteredUsers ?? users).map((u, i) => (
              <UserRowEntry
                key={u.id}
                ordinal={i + 1}
                user={u}
                groupNameById={groupNameById}
                isDefaultById={isDefaultById}
                isMe={actor?.id === u.id}
              />
            ))}
          </ol>
        )}
      </section>

      <section className="mt-12">
        <SectionHeader
          label="Väntar på svar"
          count={pendingCount}
          sublabel={pendingCount === 0 ? 'Inga öppna inbjudningar' : null}
          filteredCount={
            filterTokens.length > 0 && filteredInvitations
              ? filteredInvitations.length
              : null
          }
        />
        {invitations === null ? (
          <div className="ledger-meta mt-6 pl-14">Laddar…</div>
        ) : invitations.length === 0 ? null : (filteredInvitations ?? invitations).length === 0 ? (
          <div className="ledger-meta mt-6 pl-14">
            Inga inbjudningar matchar “{query.trim()}”.
          </div>
        ) : (
          <ol className="mt-6">
            {(filteredInvitations ?? invitations).map((inv, i) => (
              <InvitationRow
                key={inv.id}
                ordinal={i + 1}
                invitation={inv}
                groupNameById={groupNameById}
                canCancel={canCancel(inv)}
                busy={busy}
                onCancel={() => void cancelInvitation(inv.id, inv.email_address)}
              />
            ))}
          </ol>
        )}
      </section>

      <Colophon />
    </div>
  );
}

/* ─────────────────────────── Masthead ─────────────────────────── */

function Masthead(props: {
  count: number | null;
  pendingCount: number;
  canInvite: boolean;
  inviteOpen: boolean;
  onInvite: () => void;
  busy: boolean;
}) {
  return (
    <header className="relative pl-14">
      <div className="ledger-meta absolute left-14 top-0 flex items-center gap-2">
        <span aria-hidden>※</span>
        <span>Register · Personalia</span>
      </div>

      <h1
        className="ledger-display mt-7 text-[clamp(2.2rem,4.5vw,3.4rem)] leading-[0.95]"
        style={{ fontVariationSettings: "'opsz' 144, 'SOFT' 50, 'wght' 380" }}
      >
        Användare
        <span
          className="ml-3 align-top text-[0.4em]"
          style={{ fontVariationSettings: "'opsz' 9, 'wght' 400" }}
        >
          {props.count !== null ? String(props.count).padStart(2, '0') : '—'}
        </span>
      </h1>

      <div className="mt-2 flex flex-wrap items-baseline justify-end gap-6">
        <div className="ml-auto flex items-center gap-6">
          {props.pendingCount > 0 && (
            <Tally label="Väntar" value={props.pendingCount} accent />
          )}
          {props.canInvite && (
            <button
              type="button"
              onClick={props.onInvite}
              disabled={props.busy}
              className="ledger-btn-primary"
            >
              {props.inviteOpen ? '— Stäng' : '+ Bjud in'}
            </button>
          )}
        </div>
      </div>

      <div className="ledger-rule mt-6" />
    </header>
  );
}

function Tally({ label, value, accent }: { label: string; value: number; accent?: boolean }) {
  return (
    <div className="text-right">
      <div
        className="ledger-mono leading-none"
        style={{
          fontSize: '22px',
          color: accent ? 'var(--ledger-amber)' : 'var(--ledger-ink)',
        }}
      >
        {String(value).padStart(2, '0')}
      </div>
      <div className="ledger-meta mt-1">{label}</div>
    </div>
  );
}

/* ─────────────────────────── Section header ─────────────────────────── */

function SectionHeader(props: {
  label: string;
  count: number | null;
  sublabel?: string | null;
  filteredCount?: number | null;
}) {
  return (
    <div className="flex items-baseline gap-4 pl-14">
      <h2
        className="ledger-display"
        style={{
          fontSize: '20px',
          fontVariationSettings: "'opsz' 30, 'SOFT' 40, 'wght' 400",
        }}
      >
        {props.label}
      </h2>
      {props.count !== null && (
        <span
          className="ledger-mono"
          style={{ fontSize: '12px', color: 'var(--ledger-ink-mute)' }}
        >
          {props.filteredCount != null
            ? `${String(props.filteredCount).padStart(2, '0')} / ${String(props.count).padStart(2, '0')}`
            : String(props.count).padStart(2, '0')}
        </span>
      )}
      {props.sublabel && (
        <span
          className="ledger-meta"
          style={{ color: 'var(--ledger-ink-mute)' }}
        >
          {props.sublabel}
        </span>
      )}
      <span className="flex-1 self-end pb-[5px]">
        <span className="ledger-rule block" />
      </span>
    </div>
  );
}

/* ─────────────────────────── User row ─────────────────────────── */

function UserRowEntry(props: {
  ordinal: number;
  user: UserRow;
  groupNameById: Map<string, string>;
  isDefaultById: Map<string, boolean>;
  isMe: boolean;
}) {
  const { user: u } = props;
  // Limbo per ADR-0017: no memberships at all AND not an org_admin.
  // Default-group-only membership IS real access; it's no longer limbo.
  const inLimbo = u.memberships.length === 0 && u.tenantRole !== 'org_admin';
  const resolvedMemberships = u.memberships
    .map((m) => {
      const name = props.groupNameById.get(m.groupId);
      if (!name) return null;
      return {
        groupId: m.groupId,
        name,
        role: m.role,
        isDefault: !!props.isDefaultById.get(m.groupId),
      };
    })
    .filter(
      (
        m,
      ): m is {
        groupId: string;
        name: string;
        role: 'admin' | 'normal';
        isDefault: boolean;
      } => m !== null,
    );

  return (
    <li className="ledger-row">
      <div className="grid grid-cols-[40px_minmax(0,1.4fr)_minmax(0,1.6fr)_minmax(0,auto)] items-center gap-6 py-2 pl-2 pr-3">
        <span
          className="ledger-idx ledger-mono"
          style={{ color: 'var(--ledger-ink-mute)', fontSize: '11px' }}
        >
          {String(props.ordinal).padStart(2, '0')}
        </span>

        <div className="min-w-0">
          <div className="flex items-baseline gap-2">
            <span
              className="truncate"
              style={{
                fontFamily: 'var(--ledger-serif)',
                fontSize: '14px',
                fontVariationSettings: "'opsz' 22, 'wght' 440",
              }}
            >
              {u.name ?? u.email}
            </span>
            {props.isMe && (
              <span
                className="ledger-meta shrink-0"
                style={{ color: 'var(--ledger-accent)' }}
              >
                ★
              </span>
            )}
          </div>
          {u.name && (
            <div
              className="ledger-mono truncate"
              style={{ fontSize: '10.5px', color: 'var(--ledger-ink-mute)' }}
            >
              {u.email}
            </div>
          )}
        </div>

        <div className="min-w-0">
          {resolvedMemberships.length > 0 ? (
            <GroupSummary memberships={resolvedMemberships} userId={u.id} />
          ) : (
            <span
              className="ledger-meta"
              style={{ color: 'var(--ledger-ink-mute)' }}
            >
              —
            </span>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2 justify-self-end">
          {u.tenantRole === 'org_admin' && <Badge tone="ink">Org admin</Badge>}
          {inLimbo && <Badge tone="amber">Väntar åtkomst</Badge>}
        </div>
      </div>
      <div className="ledger-rule" />
    </li>
  );
}

function GroupSummary({
  memberships,
  userId,
}: {
  memberships: ReadonlyArray<{
    groupId: string;
    name: string;
    role: 'admin' | 'normal';
    isDefault: boolean;
  }>;
  userId: string;
}) {
  const navigate = useNavigate();
  return (
    <span
      className="ledger-mono truncate"
      style={{
        fontSize: '11px',
        color: 'var(--ledger-ink-soft)',
        display: 'inline-block',
        maxWidth: '100%',
      }}
      title={memberships
        .map(
          (m) =>
            `${m.name}${m.role === 'admin' ? ' (admin)' : ''}${m.isDefault ? ' (default)' : ''}`,
        )
        .join(', ')}
    >
      {memberships.map((m, i) => (
        <span key={m.groupId}>
          {i > 0 && <span style={{ color: 'var(--ledger-ink-mute)' }}>, </span>}
          <button
            type="button"
            onClick={() =>
              navigate(`/groups?group=${m.groupId}&user=${userId}`)
            }
            className="ledger-group-link"
            style={{
              color: m.isDefault ? 'var(--ledger-ink-mute)' : undefined,
            }}
          >
            {m.name}
          </button>
          {m.role === 'admin' && (
            <span style={{ color: 'var(--ledger-accent)' }}> · admin</span>
          )}
        </span>
      ))}
    </span>
  );
}

function SearchInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return (
    <div className="relative max-w-md">
      <span
        aria-hidden
        className="ledger-mono pointer-events-none absolute left-3 top-1/2 -translate-y-1/2"
        style={{ fontSize: '12px', color: 'var(--ledger-ink-mute)' }}
      >
        ⌕
      </span>
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="ledger-mono w-full border bg-transparent py-1.5 pl-8 pr-8 focus:outline-none"
        style={{
          fontSize: '12px',
          color: 'var(--ledger-ink)',
          borderColor: 'var(--ledger-rule)',
        }}
      />
      {value.length > 0 && (
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label="Rensa sökning"
          className="absolute right-2 top-1/2 -translate-y-1/2"
          style={{
            fontSize: '14px',
            color: 'var(--ledger-ink-mute)',
            lineHeight: 1,
          }}
        >
          ×
        </button>
      )}
    </div>
  );
}

function Badge({
  children,
  tone,
}: {
  children: React.ReactNode;
  tone: 'ink' | 'amber' | 'accent';
}) {
  const bg =
    tone === 'ink'
      ? 'var(--ledger-ink)'
      : tone === 'amber'
      ? 'var(--ledger-amber)'
      : 'var(--ledger-accent)';
  return (
    <span
      className="ledger-mono"
      style={{
        background: bg,
        color: '#fefcf6',
        fontSize: '9px',
        padding: '2px 6px',
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
      }}
    >
      {children}
    </span>
  );
}

/* ─────────────────────────── Invitation row ─────────────────────────── */

function InvitationRow(props: {
  ordinal: number;
  invitation: PendingInvitation;
  groupNameById: Map<string, string>;
  canCancel: boolean;
  busy: boolean;
  onCancel: () => void;
}) {
  const inv = props.invitation;
  const groupNames = (inv.public_metadata.group_ids ?? [])
    .map((id) => props.groupNameById.get(id))
    .filter((n): n is string => typeof n === 'string' && n.length > 0);
  const tenantRole = inv.public_metadata.tenant_role;
  const sent = new Date(inv.created_at);
  const sentLabel = sent.toLocaleDateString('sv-SE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  return (
    <li className="ledger-row">
      <div className="grid grid-cols-[40px_minmax(0,1.4fr)_minmax(0,1.6fr)_auto_auto] items-center gap-6 py-2 pl-2 pr-3">
        <span
          className="ledger-idx ledger-mono"
          style={{ color: 'var(--ledger-ink-mute)', fontSize: '11px' }}
        >
          {String(props.ordinal).padStart(2, '0')}
        </span>

        <div className="min-w-0">
          <span
            className="truncate"
            style={{
              fontFamily: 'var(--ledger-serif)',
              fontSize: '14px',
              fontVariationSettings: "'opsz' 22, 'wght' 400",
              display: 'inline-block',
              maxWidth: '100%',
            }}
          >
            {inv.public_metadata.name ?? inv.email_address}
          </span>
          {inv.public_metadata.name && (
            <span
              className="ledger-mono block truncate"
              style={{ fontSize: '10.5px', color: 'var(--ledger-ink-mute)' }}
            >
              {inv.email_address}
            </span>
          )}
        </div>

        <div className="min-w-0">
          {groupNames.length > 0 ? (
            <span
              className="ledger-mono truncate"
              style={{
                fontSize: '11px',
                color: 'var(--ledger-ink-soft)',
                display: 'inline-block',
                maxWidth: '100%',
              }}
              title={groupNames.join(', ')}
            >
              {groupNames.join(', ')}
            </span>
          ) : (
            <span className="ledger-meta" style={{ color: 'var(--ledger-ink-mute)' }}>
              —
            </span>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-3 whitespace-nowrap">
          {tenantRole === 'org_admin' && <Badge tone="ink">Org admin</Badge>}
          <span
            className="ledger-mono"
            style={{ fontSize: '11px', color: 'var(--ledger-ink-mute)' }}
          >
            Skickad {sentLabel}
          </span>
        </div>

        {props.canCancel ? (
          <button
            type="button"
            onClick={props.onCancel}
            disabled={props.busy}
            className="ledger-btn-link justify-self-end"
            style={{ color: 'var(--ledger-ember)' }}
          >
            Återkalla
          </button>
        ) : (
          <span
            className="ledger-meta justify-self-end"
            style={{ color: 'var(--ledger-ink-mute)' }}
            title="Endast org-admin eller gruppadmin för målgruppen kan återkalla denna inbjudan."
          >
            Utanför ramverk
          </span>
        )}
      </div>
      <div className="ledger-rule" />
    </li>
  );
}

/* ─────────────────────────── Invite panel ─────────────────────────── */

function InvitePanel(props: {
  email: string;
  onEmailChange: (v: string) => void;
  name: string;
  onNameChange: (v: string) => void;
  tenantRole: 'org_admin' | 'normal';
  onTenantRoleChange: (r: 'org_admin' | 'normal') => void;
  /** Only org-admins can elevate someone to org_admin; for group-admin
   *  inviters the checkbox is hidden (backend enforces the same). */
  showOrgAdminCheckbox: boolean;
  groupRole: 'admin' | 'normal';
  onGroupRoleChange: (r: 'admin' | 'normal') => void;
  assignableGroups: GroupRow[];
  selectedGroupIds: string[];
  onSelectedGroupIdsChange: (ids: string[]) => void;
  busy: boolean;
  onCancel: () => void;
  onSubmit: () => void;
}) {
  // Field-level validation mirrors the backend `users.invite` zod schema
  // (email, name 1–200, group_ids min 1). We surface these client-side so an
  // incomplete form explains itself instead of failing silently.
  const [touched, setTouched] = useState<{
    name?: boolean;
    email?: boolean;
    groups?: boolean;
  }>({});
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const fieldErrors = inviteFieldErrors({
    name: props.name,
    email: props.email,
    selectedGroupIds: props.selectedGroupIds,
  });
  const { name: nameError, email: emailError, groups: groupError } = fieldErrors;

  // Show an error only once the field has been touched or a submit was
  // attempted — we don't want to yell at a pristine, just-opened form.
  const showNameError = (touched.name || submitAttempted) && nameError;
  const showEmailError = (touched.email || submitAttempted) && emailError;
  const showGroupError = (touched.groups || submitAttempted) && groupError;

  const toggleGroup = (id: string) => {
    setTouched((t) => ({ ...t, groups: true }));
    props.onSelectedGroupIdsChange(
      props.selectedGroupIds.includes(id)
        ? props.selectedGroupIds.filter((x) => x !== id)
        : [...props.selectedGroupIds, id],
    );
  };

  const handleSubmit = () => {
    setSubmitAttempted(true);
    // Reveal every field's error at once; only dispatch when the form is clean.
    if (hasInviteErrors(fieldErrors)) return;
    props.onSubmit();
  };

  return (
    <section
      className="ledger-unfold mt-8 grid grid-cols-1 gap-8 md:grid-cols-[1fr_300px] md:gap-12 pl-4 pr-4 pb-8 pt-7 md:pl-14 md:pr-6"
      style={{
        background: 'var(--ledger-paper-soft)',
        borderTop: '1px solid var(--ledger-rule-strong)',
        borderBottom: '1px solid var(--ledger-rule-strong)',
      }}
    >
      <div>
        <div className="ledger-meta mb-1">Ny inbjudan</div>
        <h2
          className="ledger-display"
          style={{
            fontSize: '28px',
            fontVariationSettings: "'opsz' 50, 'SOFT' 40, 'wght' 380",
          }}
        >
          <em
            className="not-italic"
            style={{ fontStyle: 'italic', fontFamily: 'var(--ledger-serif)' }}
          >
            Bjud in
          </em>{' '}
          en kollega
        </h2>

        <div className="mt-6 max-w-md">
          <label className="ledger-meta mb-1 block">Namn</label>
          <input
            type="text"
            value={props.name}
            onChange={(e) => props.onNameChange(e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, name: true }))}
            placeholder="För- och efternamn"
            className="ledger-input"
            style={{
              fontFamily: 'var(--ledger-serif)',
              fontSize: '18px',
              fontVariationSettings: "'opsz' 28, 'wght' 380",
            }}
            disabled={props.busy}
            aria-invalid={showNameError ? true : undefined}
            autoFocus
          />
          {showNameError && <FieldError message={nameError!} />}
        </div>

        <div className="mt-6 max-w-md">
          <label className="ledger-meta mb-1 block">E-postadress</label>
          <input
            type="email"
            value={props.email}
            onChange={(e) => props.onEmailChange(e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, email: true }))}
            placeholder="namn@företag.se"
            className="ledger-input"
            style={{
              fontFamily: 'var(--ledger-serif)',
              fontSize: '18px',
              fontVariationSettings: "'opsz' 28, 'wght' 380",
            }}
            disabled={props.busy}
            aria-invalid={showEmailError ? true : undefined}
          />
          {showEmailError && <FieldError message={emailError!} />}
        </div>

        <div className="mt-6 max-w-md">
          <div className="ledger-meta mb-2">Roll i grupp</div>
          <div className="flex items-center gap-5">
            {(
              [
                ['normal', 'Medlem'],
                ['admin', 'Gruppadmin'],
              ] as const
            ).map(([value, label]) => (
              <label
                key={value}
                className="flex cursor-pointer items-center gap-2"
                style={{ fontFamily: 'var(--ledger-serif)', fontSize: '14px' }}
              >
                <input
                  type="radio"
                  name="group-role"
                  value={value}
                  checked={props.groupRole === value}
                  onChange={() => props.onGroupRoleChange(value)}
                  disabled={props.busy}
                />
                <span>{label}</span>
              </label>
            ))}
          </div>
          <p
            className="mt-1"
            style={{
              fontFamily: 'var(--ledger-serif)',
              fontStyle: 'italic',
              fontSize: '12px',
              color: 'var(--ledger-ink-mute)',
            }}
          >
            {props.groupRole === 'admin'
              ? 'Får hantera medlemskap och inställningar i de valda grupperna.'
              : 'Får arbeta med grupperna; inga administrativa rättigheter.'}
          </p>
        </div>

        {props.showOrgAdminCheckbox && (
          <div className="mt-5 max-w-md">
            <div className="ledger-meta mb-2">Organisationsroll</div>
            <label
              className="flex cursor-pointer items-start gap-3"
              style={{ fontFamily: 'var(--ledger-serif)', fontSize: '14px' }}
            >
              <input
                type="checkbox"
                checked={props.tenantRole === 'org_admin'}
                onChange={(e) =>
                  props.onTenantRoleChange(e.target.checked ? 'org_admin' : 'normal')
                }
                disabled={props.busy}
                className="mt-1"
              />
              <span>
                <span>Org-admin</span>
                <span
                  className="block"
                  style={{
                    fontFamily: 'var(--ledger-serif)',
                    fontStyle: 'italic',
                    fontSize: '12px',
                    color: 'var(--ledger-ink-mute)',
                    marginTop: '2px',
                  }}
                >
                  Full åtkomst att hantera alla grupper, användare och inställningar i tenanten. Lämna omarkerad för en vanlig användare vars åtkomst styrs av grupperna.
                </span>
              </span>
            </label>
          </div>
        )}

        <div className="mt-8 flex items-center gap-3">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={props.busy}
            className="ledger-btn-primary"
          >
            Skicka inbjudan
          </button>
          <button
            type="button"
            onClick={props.onCancel}
            disabled={props.busy}
            className="ledger-btn-ghost"
          >
            Avbryt
          </button>
          <span
            className="ledger-meta ml-3"
            style={{
              color: showGroupError
                ? 'var(--ledger-ember)'
                : 'var(--ledger-ink-mute)',
            }}
          >
            {props.selectedGroupIds.length === 0
              ? showGroupError
                ? groupError
                : 'Ingen grupp vald'
              : `${String(props.selectedGroupIds.length).padStart(2, '0')} ${
                  props.selectedGroupIds.length === 1 ? 'grupp' : 'grupper'
                } vald${props.selectedGroupIds.length === 1 ? '' : 'a'}`}
          </span>
        </div>
      </div>

      <aside
        className="border-l pl-8"
        style={{ borderColor: 'var(--ledger-rule)' }}
      >
        <div className="ledger-meta mb-3">Tilldela grupp(er)</div>
        {showGroupError && <FieldError message={groupError!} />}
        {props.assignableGroups.length === 0 ? (
          <p
            className="ledger-meta italic"
            style={{
              textTransform: 'none',
              letterSpacing: 0,
              fontFamily: 'var(--ledger-serif)',
              fontStyle: 'italic',
              fontSize: '13px',
              color: 'var(--ledger-ink-mute)',
            }}
          >
            Du har inga grupper du kan tilldela. En org-admin behöver göra dig till gruppadmin först.
          </p>
        ) : (
          <ul
            className="max-h-72 overflow-y-auto"
            style={{ scrollbarColor: 'var(--ledger-rule-strong) transparent' }}
          >
            {props.assignableGroups.map((g) => {
              const checked = props.selectedGroupIds.includes(g.id);
              return (
                <li key={g.id}>
                  <label
                    className="flex cursor-pointer items-center gap-3 py-2.5"
                    style={{ borderBottom: '1px solid var(--ledger-rule)' }}
                  >
                    <span
                      className="inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center border"
                      style={{
                        borderColor: checked
                          ? 'var(--ledger-ink)'
                          : 'var(--ledger-rule-strong)',
                        background: checked ? 'var(--ledger-ink)' : 'transparent',
                      }}
                    >
                      {checked && (
                        <span
                          className="ledger-mono"
                          style={{
                            color: 'var(--ledger-lime)',
                            fontSize: '9px',
                            lineHeight: 1,
                          }}
                        >
                          ✓
                        </span>
                      )}
                    </span>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleGroup(g.id)}
                      className="sr-only"
                    />
                    <span className="min-w-0 flex-1">
                      <span
                        className="block truncate"
                        style={{
                          fontFamily: 'var(--ledger-serif)',
                          fontSize: '14px',
                          fontVariationSettings: "'opsz' 20, 'wght' 400",
                        }}
                      >
                        {g.name}
                        {g.is_default && (
                          <span
                            className="ledger-meta ml-2"
                            style={{ color: 'var(--ledger-ink-mute)' }}
                          >
                            default
                          </span>
                        )}
                      </span>
                      <span
                        className="ledger-mono block"
                        style={{ fontSize: '10px', color: 'var(--ledger-ink-mute)' }}
                      >
                        {String(g.member_count).padStart(2, '0')} medlem
                        {g.member_count === 1 ? '' : 'mar'}
                      </span>
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        )}
      </aside>
    </section>
  );
}

/* ─────────────────────────── Bits ─────────────────────────── */

function FieldError({ message }: { message: string }) {
  return (
    <p
      role="alert"
      className="mt-1.5"
      style={{
        fontFamily: 'var(--ledger-serif)',
        fontStyle: 'italic',
        fontSize: '12px',
        color: 'var(--ledger-ember)',
      }}
    >
      {message}
    </p>
  );
}

function ErrorStrip({
  message,
  onDismiss,
  tone,
}: {
  message: string;
  onDismiss: () => void;
  tone: 'ember' | 'muted';
}) {
  const stripe =
    tone === 'ember' ? 'var(--ledger-ember)' : 'var(--ledger-accent)';
  const label = tone === 'ember' ? 'Fel' : 'OK';
  return (
    <div
      className="mt-6 flex items-baseline gap-4 pl-14 pr-6 py-3"
      style={{
        background: 'var(--ledger-paper-soft)',
        borderLeft: `3px solid ${stripe}`,
      }}
    >
      <span className="ledger-meta" style={{ color: stripe }}>
        {label}
      </span>
      <span
        style={{
          fontFamily: 'var(--ledger-serif)',
          fontStyle: 'italic',
          fontSize: '14px',
          color: 'var(--ledger-ink-soft)',
        }}
        className="flex-1"
      >
        {message}
      </span>
      <button
        type="button"
        onClick={onDismiss}
        className="ledger-btn-link"
        style={{ color: 'var(--ledger-ink-mute)' }}
      >
        Dölj
      </button>
    </div>
  );
}

function Colophon() {
  return (
    <div className="mt-20 pl-14 pr-6" style={{ color: 'var(--ledger-ink-mute)' }}>
      <div className="ledger-rule mb-4" />
      <div className="flex items-baseline justify-between">
        <span className="ledger-meta">Slut på register</span>
        <span className="ledger-mono" style={{ fontSize: '10px' }}>
          REGTA · USERS · v1
        </span>
      </div>
    </div>
  );
}
